import { useState, useCallback } from "react";
import { createPeriodState } from "../../../../utils/periodDateUtils";
import { todayStr } from "./utils";

const ss = {
  get: (key, fallback) => {
    try {
      const v = sessionStorage.getItem("rp_" + key);
      return v !== null ? JSON.parse(v) : fallback;
    } catch {
      return fallback;
    }
  },
  set: (key, val) => {
    try {
      sessionStorage.setItem("rp_" + key, JSON.stringify(val));
    } catch {}
  },
};

const persistPeriod = (period) => {
  ss.set("periodMode", period.mode);
  ss.set("anchorDate", period.anchorDate);
  ss.set("startDate", period.startDate);
  ss.set("endDate", period.endDate);
};

const defaultReportPeriod = () => createPeriodState("month", todayStr());

const initReportPeriod = () => {
  const storedMode = ss.get("periodMode", null);
  const storedAnchor = ss.get("anchorDate", null);
  const startDate = ss.get("startDate", null);
  const endDate = ss.get("endDate", null);

  if (storedMode) {
    return createPeriodState(
      storedMode,
      storedAnchor || todayStr(),
      storedMode === "custom" && startDate && endDate
        ? { startDate, endDate }
        : undefined,
    );
  }

  if (startDate && endDate) {
    return createPeriodState("custom", startDate, { startDate, endDate });
  }

  return defaultReportPeriod();
};

/** Persist report filters before navigating from live pages (read on next mount). */
export function seedReportFilters({
  machineId,
  shiftId,
  startDate,
  endDate,
} = {}) {
  if (startDate && endDate) {
    const period = createPeriodState("custom", startDate, { startDate, endDate });
    persistPeriod(period);
  } else if (startDate) {
    const day = String(startDate);
    persistPeriod(createPeriodState("day", day));
  }
  if (machineId) ss.set("machineIds", [String(machineId)]);
  if (shiftId != null && shiftId !== "") ss.set("shiftId", String(shiftId));
}

const makeSetter = (key, dispatch) => (v) => {
  dispatch((prev) => {
    const next = typeof v === "function" ? v(prev) : v;
    ss.set(key, next);
    return next;
  });
};

export function useReportFilters() {
  const [period, _sp] = useState(initReportPeriod);
  const [machineIds, _sm] = useState(() => ss.get("machineIds", []));
  const [shiftId, _ssi] = useState(() => ss.get("shiftId", ""));
  const [transFromStatus, _sfs] = useState(() => ss.get("transFromStatus", ""));
  const [transToStatus, _sts] = useState(() => ss.get("transToStatus", ""));
  const [transModelId, _stm] = useState(() => ss.get("transModelId", ""));

  const setPeriod = useCallback((next) => {
    _sp(next);
    persistPeriod(next);
  }, []);

  const setStartDate = useCallback((v) => {
    _sp((prev) => {
      const start = typeof v === "function" ? v(prev.startDate) : v;
      const next = createPeriodState("custom", start, {
        startDate: start,
        endDate: prev.endDate,
      });
      persistPeriod(next);
      return next;
    });
  }, []);

  const setEndDate = useCallback((v) => {
    _sp((prev) => {
      const end = typeof v === "function" ? v(prev.endDate) : v;
      const next = createPeriodState("custom", prev.startDate, {
        startDate: prev.startDate,
        endDate: end,
      });
      persistPeriod(next);
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setMachineIds = useCallback(makeSetter("machineIds", _sm), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setShiftId = useCallback(makeSetter("shiftId", _ssi), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setTransFromStatus = useCallback(
    makeSetter("transFromStatus", _sfs),
    [],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setTransToStatus = useCallback(makeSetter("transToStatus", _sts), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setTransModelId = useCallback(makeSetter("transModelId", _stm), []);

  const reset = useCallback(() => {
    setPeriod(defaultReportPeriod());
    setMachineIds([]);
    setShiftId("");
    setTransFromStatus("");
    setTransToStatus("");
    setTransModelId("");
  }, [
    setPeriod,
    setMachineIds,
    setShiftId,
    setTransFromStatus,
    setTransToStatus,
    setTransModelId,
  ]);

  return {
    startDate: period.startDate,
    endDate: period.endDate,
    periodMode: period.mode,
    anchorDate: period.anchorDate,
    setPeriod,
    setStartDate,
    setEndDate,
    machineIds,
    setMachineIds,
    shiftId,
    setShiftId,
    transFromStatus,
    setTransFromStatus,
    transToStatus,
    setTransToStatus,
    transModelId,
    setTransModelId,
    reset,
  };
}

export { defaultReportPeriod };
